import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Heart, MessageSquare, Share2, MoreVertical, Globe, Activity, Plus, Bookmark, EyeOff, X, User, Download, FileText, Trash2, ChevronDown } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { generateMSNAvatar } from '../utils/uiUtils';
import { LinkEmbed } from './LinkEmbed';
import { useUIStore } from '../stores/uiStore';

interface FeedPostCardProps {
  post: {
    id: string;
    authorId: string;
    authorName: string;
    authorAvatar: string;
    content: string;
    createdAt: any;
    expiresAt: number | null;
    likes: Record<string, string | boolean>;
    replyCount: number;
    replies?: any[];
  };
  onLike: (postId: string) => void;
  onReply: (postId: string, text: string) => void;
  onSave: (postId: string) => void;
  onHideUser: (userId: string) => void;
  onViewProfile: (userId: string) => void;
  onFollow: (userId: string) => void;
  onDelete?: (postId: string) => void;
  isFollowed: boolean;
  currentUserId: string;
  getUserFontStyle?: (userId: string) => React.CSSProperties | undefined;
  onUserContextMenu?: (e: React.MouseEvent, userId: string) => void;
}

const renderContent = (content: string, onDownload: (url: string, name: string) => void, setViewingImageUrl: (url: string | null) => void) => {
  const emoteMap: Record<string, string> = {
    ':smile:': '😊',
    ':grin:': '😁',
    ':heart:': '❤️',
    ':fire:': '🔥',
    ':lol:': '😂'
  };
  
  let formattedContent = content;

  Object.keys(emoteMap).forEach(key => {
    formattedContent = formattedContent.split(key).join(emoteMap[key]);
  });

  return (
    <ReactMarkdown
      rehypePlugins={[rehypeRaw]}
      components={{
        img: ({ node, ...props }: any) => {
          const isVideo = props.alt === 'video';
          const isAudio = props.alt === 'audio';
          const isFile = props.alt?.startsWith('file:');
          
          if (isVideo) {
            return (
              <div className="my-4 rounded-2xl overflow-hidden border border-white/10 bg-black shadow-2xl relative group">
                <video src={props.src} controls className="w-full max-h-[500px] object-contain" />
                <button onClick={() => onDownload(props.src, 'video.mp4')} className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-[var(--neon)] hover:text-black rounded-lg opacity-0 group-hover:opacity-100 transition-all">
                  <Download size={16} />
                </button>
              </div>
            );
          }
          
          if (isAudio) {
            return (
              <div className="my-4 p-4 rounded-2xl bg-white/5 border border-white/10 flex items-center gap-4 group">
                <div className="w-10 h-10 rounded-full bg-[var(--neon)]/20 flex items-center justify-center text-[var(--neon)]">
                  <Activity size={20} className="animate-pulse" />
                </div>
                <audio src={props.src} controls className="flex-1 accent-[var(--neon)]" />
                <button onClick={() => onDownload(props.src, 'audio.mp3')} className="p-2 bg-white/5 hover:bg-[var(--neon)] hover:text-black rounded-lg opacity-0 group-hover:opacity-100 transition-all">
                  <Download size={16} />
                </button>
              </div>
            );
          }

          if (isFile) {
            const fileName = props.alt?.split(':')[1] || 'attachment';
            return <FileNode url={props.src} name={fileName} onDownload={onDownload} />;
          }
          
          // Regular image - render beautifully scaled, wrapping the image content perfectly
          return (
            <div 
              className="my-4 rounded-2xl overflow-hidden shadow-2xl relative group border border-white/10 bg-[#070a13]/80 inline-block max-w-full cursor-pointer"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setViewingImageUrl(props.src); }}
            >
              <img
                src={props.src}
                alt={props.alt || ''}
                className="h-auto max-h-[500px] max-w-full object-contain block"
              />
              <button
                onClick={(e) => { e.stopPropagation(); onDownload(props.src, 'image.png'); }}
                className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-[var(--neon)] hover:text-black rounded-lg opacity-0 group-hover:opacity-100 transition-all"
              >
                <Download size={16} />
              </button>
            </div>
          );
        }
      }}
    >
      {formattedContent}
    </ReactMarkdown>
  );
};

const FileNode = ({ url, name, onDownload }: { url: string, name: string, onDownload: (url: string, name: string) => void }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [content, setContent] = useState<string | null>(null);

  const toggleExpand = async () => {
    if (!isExpanded && !content) {
      try {
        const res = await fetch(url);
        const text = await res.text();
        setContent(text.substring(0, 5000)); // Limit preview
      } catch (err) { setContent("Could not preview file content."); }
    }
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="my-4 bg-white/5 border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
      <div className="p-4 flex items-center justify-between gap-4 bg-white/5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/5 rounded-lg text-white/40">
            <FileText size={20} />
          </div>
          <div>
            <p className="text-xs font-black text-white/90 truncate max-w-[200px] uppercase tracking-tighter">{name}</p>
            <p className="text-[10px] text-white/30 font-mono">NEURAL_ATTACHMENT</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={toggleExpand} className="px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/60 text-[10px] font-bold rounded-lg transition-colors">
            {isExpanded ? 'CLOSE' : 'VIEW'}
          </button>
          <button onClick={() => onDownload(url, name)} className="p-1.5 bg-[var(--neon)] text-black rounded-lg hover:scale-110 transition-transform">
            <Download size={16} />
          </button>
        </div>
      </div>
      <AnimatePresence>
        {isExpanded && (
          <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
            <pre className="p-4 text-[10px] font-mono text-white/60 bg-black/40 whitespace-pre-wrap break-all border-t border-white/5">
              {content || 'Loading neural data...'}
            </pre>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export function FeedPostCard({ post, onLike, onReply, onSave, onHideUser, onViewProfile, onFollow, onDelete, isFollowed, currentUserId, getUserFontStyle, onUserContextMenu }: FeedPostCardProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [showReplyComposer, setShowReplyComposer] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [showDownloadPrompt, setShowDownloadPrompt] = useState<{ url: string, name: string } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { setViewingImageUrl } = useUIStore();
  
  const isOwn = post.authorId === currentUserId;
  const isLiked = post.likes?.[currentUserId];
  const likeCount = Object.keys(post.likes || {}).length;
  
  const timeStr = post.createdAt?.toDate
    ? post.createdAt.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : post.createdAt
      ? new Date(post.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : 'Now';

  const handleSendReply = () => {
    if (!replyText.trim()) return;
    onReply(post.id, replyText);
    setReplyText('');
    setShowReplyComposer(false);
  };

  const handleShare = async () => {
    const shareData = {
      title: `Post by ${post.authorName} on PsydeChat`,
      text: post.content.substring(0, 100) + '...',
      url: window.location.href + (window.location.href.includes('?') ? '&' : '?') + 'post=' + post.id
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      }
    } catch (err) {
      console.error('Error sharing:', err);
    }
  };

  const executeDownload = (url: string, name: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowDownloadPrompt(null);
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
    setShowMenu(false);
  };

  const confirmDelete = () => {
    onDelete?.(post.id);
    setShowDeleteConfirm(false);
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10, scale: 0.98 }}
      className={`p-6 mb-6 mx-2 rounded-[2rem] bg-black/40 backdrop-blur-xl border border-white/5 shadow-2xl transition-all group relative hover:bg-black/60 hover:border-[var(--neon)]/20 ${showMenu ? 'z-[100]' : 'z-10'}`}
    >
      <div className="flex gap-4">
        <button onClick={() => onViewProfile(post.authorId)} onContextMenu={(e) => onUserContextMenu?.(e, post.authorId)} className="w-12 h-12 rounded-2xl overflow-hidden border border-white/10 shrink-0 shadow-lg cursor-pointer hover:opacity-80 transition-opacity hover:border-[var(--neon)]/40">
          <img src={post.authorAvatar || generateMSNAvatar(post.authorId)} alt="" className="w-full h-full object-cover" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <button 
                onClick={() => onViewProfile(post.authorId)} 
                onContextMenu={(e) => onUserContextMenu?.(e, post.authorId)}
                className="font-black text-sm text-white uppercase tracking-tight hover:text-[var(--neon)] transition-colors"
                style={getUserFontStyle?.(post.authorId)}
              >
                {post.authorName}
              </button>
              <span className="text-[10px] text-white/30 font-mono">• {timeStr}</span>
              {post.expiresAt && (
                <div className="flex items-center gap-1 px-1.5 py-0.5 bg-red-500/10 border border-red-500/20 rounded text-[8px] font-black text-red-400 uppercase animate-pulse">
                  <Activity size={8} /> TTL
                </div>
              )}
            </div>
            <div className="flex items-center gap-3 relative">
              {!isOwn && (
                <button 
                  onClick={() => onFollow(post.authorId)} 
                  className={`transition-colors ${isFollowed ? 'text-[var(--neon)]' : 'text-white/20 hover:text-white'}`}
                  title={isFollowed ? "Unfollow Node" : "Follow Node"}
                >
                  <Plus size={16} />
                </button>
              )}
              <button onClick={() => setShowMenu(!showMenu)} className="text-white/20 hover:text-white transition-colors">
                <MoreVertical size={16} />
              </button>
              
              <AnimatePresence>
                {showMenu && (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="absolute right-0 top-8 bg-black/90 backdrop-blur-xl border border-white/10 rounded-2xl p-2 z-50 w-52 shadow-2xl"
                  >
                    <button onClick={() => { onViewProfile(post.authorId); setShowMenu(false); }} className="w-full text-left px-4 py-3 text-xs font-bold text-white/70 hover:text-white hover:bg-white/5 rounded-xl flex items-center gap-3">
                      <User size={14} /> View Profile
                    </button>
                    <button onClick={() => { onSave(post.id); setShowMenu(false); }} className="w-full text-left px-4 py-3 text-xs font-bold text-white/70 hover:text-white hover:bg-white/5 rounded-xl flex items-center gap-3">
                      <Bookmark size={14} /> Save Post
                    </button>
                    {isOwn && (
                      <button onClick={handleDelete} className="w-full text-left px-4 py-3 text-xs font-bold text-red-400 hover:text-white hover:bg-red-500/20 rounded-xl flex items-center gap-3 border-t border-white/5 mt-1">
                        <Trash2 size={14} /> Delete Post
                      </button>
                    )}
                    {!isOwn && (
                      <button onClick={() => { onHideUser(post.authorId); setShowMenu(false); }} className="w-full text-left px-4 py-3 text-xs font-bold text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-xl flex items-center gap-3">
                        <EyeOff size={14} /> Hide User
                      </button>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
          
          <div className="feed-post-content text-white/80 text-sm leading-relaxed mb-4">
            {renderContent(post.content, (url, name) => setShowDownloadPrompt({ url, name }), setViewingImageUrl)}
          </div>

          {/* Render Link Embeds for any URLs in the text */}
          {(() => {
            const urls = post.content.match(/(https?:\/\/[^\s]+)/g);
            if (!urls) return null;
            // Filter out image/media links that are already handled by markdown
            const uniqueUrls = Array.from(new Set(urls)).filter(url => {
              const cleanUrl = url.replace(/[)\]}>"']+$/, '');
              return !cleanUrl.match(/\.(jpeg|jpg|gif|png|mp4|webm)$/i) && !cleanUrl.includes('supabase.co/storage');
            });
            if (uniqueUrls.length === 0) return null;
            return (
              <div className="flex flex-col gap-2 mb-4">
                {uniqueUrls.map((url, i) => (
                  <LinkEmbed key={i} url={url.replace(/[)\]}>"']+$/, '')} /> // Clean trailing punctuation
                ))}
              </div>
            );
          })()}

          <div className="flex items-center gap-6 pt-4 border-t border-white/5">
            <button 
              onClick={() => onLike(post.id)}
              className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-all group/like ${isLiked ? 'text-red-400' : 'text-white/30 hover:text-red-400'}`}
              title={(() => {
                const likers = Object.entries(post.likes || {}).map(([id, val]) => {
                  if (id === currentUserId) return 'you';
                  return typeof val === 'string' ? val : 'a user';
                });
                if (likers.length === 0) return 'Like this post';
                
                const displayLikers = likers.slice(0, 3);
                const othersCount = likers.length - displayLikers.length;
                
                let text = displayLikers.join(', ');
                if (othersCount > 0) text += ` and ${othersCount} others`;
                return `${text} liked this`;
              })()}
            >
              <Heart size={16} className={`transition-transform group-hover/like:scale-125 ${isLiked ? 'fill-current' : ''}`} />
              {likeCount > 0 && <span>{likeCount}</span>}
            </button>
            <button 
              onClick={() => setShowReplyComposer(!showReplyComposer)}
              className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-all ${showReplyComposer ? 'text-[var(--neon)]' : 'text-white/30 hover:text-white'}`}
            >
              <MessageSquare size={16} />
              {post.replyCount > 0 && post.replyCount}
            </button>
            <button 
              onClick={handleShare}
              className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-all ${isCopied ? 'text-[var(--neon)]' : 'text-white/30 hover:text-white'}`}
            >
              <Share2 size={16} />
              {isCopied ? 'COPIED' : 'SHARE'}
            </button>
          </div>

          <AnimatePresence>
            {/* Render Replies List */}
            {post.replies && post.replies.length > 0 && (
              <div className="mt-4 space-y-3 pl-4 border-l border-white/5 max-h-60 overflow-y-auto no-scrollbar">
                {post.replies.map((reply: any) => (
                  <div key={reply.id} className="flex gap-3 text-xs bg-white/[0.01] p-2 rounded-xl border border-white/5">
                    <button onClick={() => onViewProfile(reply.author_id)} className="w-6 h-6 rounded-lg overflow-hidden border border-white/10 shrink-0 shadow-md">
                      <img src={reply.author_avatar || generateMSNAvatar(reply.author_id)} alt="" className="w-full h-full object-cover" />
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <button 
                          onClick={() => onViewProfile(reply.author_id)} 
                          className="font-bold text-[11px] text-white uppercase hover:text-[var(--neon)] transition-colors"
                          style={getUserFontStyle?.(reply.author_id)}
                        >
                          {reply.author_name}
                        </button>
                        <span className="text-[8px] text-white/30 font-mono">
                          {new Date(reply.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-white/70 leading-relaxed font-sans">{reply.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {showReplyComposer && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="mt-4 overflow-hidden">
                <div className="flex gap-2">
                  <input type="text" value={replyText} onChange={(e) => setReplyText(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSendReply()} placeholder="Type a neural reply..." className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-xs text-white outline-none focus:border-[var(--neon)]" />
                  <button onClick={handleSendReply} className="px-4 py-2 bg-[var(--neon)] text-black text-xs font-black rounded-xl uppercase tracking-widest">Post</button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="bg-[var(--bg-panel)] backdrop-blur-xl border border-red-500/20 rounded-3xl p-6 w-full max-w-sm shadow-2xl">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                  <Trash2 size={18} className="text-red-400" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-white uppercase tracking-widest">Delete Post?</h3>
                  <p className="text-[10px] text-white/30 mt-0.5">This action cannot be undone</p>
                </div>
              </div>
              <div className="flex gap-3 mt-5">
                <button 
                  onClick={confirmDelete}
                  className="flex-1 bg-red-500 text-white font-black py-3 text-[10px] rounded-xl hover:bg-red-600 active:scale-95 transition-all uppercase tracking-widest"
                >
                  Delete
                </button>
                <button 
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 bg-white/5 text-white/60 font-black py-3 text-[10px] rounded-xl hover:bg-white/10 transition-all"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Download confirmation dialog */}
      <AnimatePresence>
        {showDownloadPrompt && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-[var(--bg-panel)] backdrop-blur-xl border border-[var(--stroke)] rounded-3xl p-6 w-full max-w-sm shadow-2xl">
              <h3 className="text-sm font-black text-white uppercase tracking-widest mb-2">Initialize Download?</h3>
              <p className="text-xs text-white/50 mb-6">File: {showDownloadPrompt.name}</p>
              <div className="flex gap-3">
                <button 
                  onClick={() => executeDownload(showDownloadPrompt.url, showDownloadPrompt.name)}
                  className="flex-1 bg-[var(--neon)] text-black font-black py-3 text-[10px] rounded-xl hover:scale-[1.02] active:scale-95 transition-all uppercase tracking-widest"
                >
                  Confirm Link
                </button>
                <button 
                  onClick={() => setShowDownloadPrompt(null)}
                  className="flex-1 bg-white/5 text-white/60 font-black py-3 text-[10px] rounded-xl hover:text-white transition-all"
                >
                  Abort Session
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
